import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { ItEquipment } from '@services/api'
import {
  createItEquipment,
  deleteItEquipment,
  deleteBulkItEquipment,
  findAllItEquipment,
  updateItEquipment,
} from '@state/thunks/itEquipmentThunk'

type ItEquipmentState = {
  data: ItEquipment[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
  filters: {
    search: string
    type: string
  }
  loading: boolean
  error: string | null
}

const initialState: ItEquipmentState = {
  data: [],
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {
    search: '',
    type: '',
  },
  loading: false,
  error: null,
}

const itEquipmentSlice = createSlice({
  name: 'it-equipment',
  initialState,
  reducers: {
    setItPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload
    },
    setItLimit: (state, action: PayloadAction<number>) => {
      state.pagination.limit = action.payload
      state.pagination.page = 1
    },

    setItSearch: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload
      state.pagination.page = 1
    },
    setItTypeFilter: (state, action: PayloadAction<string>) => {
      state.filters.type = action.payload
      state.pagination.page = 1
    },
    clearItFilters: (state) => {
      state.filters = { search: '', type: '' }
      state.pagination.page = 1
    },

    clearItEquipments: (state) => {
      state.data = []
      state.pagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      }
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(findAllItEquipment.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        findAllItEquipment.fulfilled,
        (state, { payload }: PayloadAction<any>) => {
          state.loading = false
          state.data = payload.data
          state.pagination = payload.meta
        },
      )
      .addCase(findAllItEquipment.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch IT equipments'
      })

      .addCase(createItEquipment.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        createItEquipment.fulfilled,
        (state, { payload }: PayloadAction<any>) => {
          state.loading = false
          state.data = [payload, ...state.data]
          state.pagination.total = state.pagination.total + 1
        },
      )
      .addCase(createItEquipment.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to create IT equipment'
      })

      .addCase(updateItEquipment.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        updateItEquipment.fulfilled,
        (state, { payload }: PayloadAction<any>) => {
          state.loading = false
          const index = state.data.findIndex(
            (_equipment) => _equipment.id === payload.id,
          )
          if (index !== -1) {
            state.data[index] = payload
          }
        },
      )
      .addCase(updateItEquipment.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update IT equipment'
      })

      .addCase(deleteItEquipment.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        deleteItEquipment.fulfilled,
        (state, { payload }: PayloadAction<any>) => {
          state.loading = false
          state.data = state.data.filter(
            (_equipment) => _equipment.id !== payload,
          )
          state.pagination.total = state.pagination.total - 1
        },
      )
      .addCase(deleteItEquipment.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete IT equipment'
      })

      .addCase(deleteBulkItEquipment.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        deleteBulkItEquipment.fulfilled,
        (state, { payload }: PayloadAction<any>) => {
          state.loading = false
          state.data = state.data.filter(
            (_equipment) => !payload.includes(_equipment.id),
          )
          state.pagination.total = state.pagination.total - payload.length
        },
      )
      .addCase(deleteBulkItEquipment.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete IT equipments'
      }),
})

export const {
  setItPage,
  setItLimit,
  setItSearch,
  setItTypeFilter,
  clearItFilters,
  clearItEquipments,
} = itEquipmentSlice.actions

export default itEquipmentSlice.reducer
